import React, { useState, useMemo } from 'react';
import { Modal } from '../ui';
import { groundTruthService } from '../../services';
import { GroundTruthDocument } from '../../types';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  document: GroundTruthDocument;
  onSaved: () => void;
}

export const GroundTruthEditFieldModal: React.FC<Props> = ({
  isOpen,
  onClose,
  document,
  onSaved,
}) => {
  const [selectedField, setSelectedField] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const fieldKeys = useMemo(() => {
    if (!document.expected_extraction) return [];
    return Object.keys(document.expected_extraction);
  }, [document.expected_extraction]);

  const currentValue = useMemo(() => {
    if (!selectedField || !document.expected_extraction) return '';
    const val = document.expected_extraction[selectedField];
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
  }, [selectedField, document.expected_extraction]);

  const handleSubmit = async () => {
    if (!selectedField) {
      toast.error('Veuillez sélectionner un champ');
      return;
    }

    setLoading(true);
    try {
      // Try to parse as JSON if it looks like an object/array, otherwise use raw string
      let parsedValue: unknown = newValue;
      if (
        (newValue.startsWith('{') && newValue.endsWith('}')) ||
        (newValue.startsWith('[') && newValue.endsWith(']'))
      ) {
        try {
          parsedValue = JSON.parse(newValue);
        } catch {
          // Keep as string if JSON parse fails
        }
      }

      await groundTruthService.editField(Number(document.id), {
        field_path: selectedField,
        new_value: parsedValue,
        reason: reason || undefined,
      });

      toast.success('Champ mis à jour avec succès');
      onSaved();
      handleClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la mise à jour';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedField('');
    setNewValue('');
    setReason('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Éditer un champ Ground Truth" size="md">
      <div className="space-y-4">
        {/* Field selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Champ à modifier
          </label>
          <select
            value={selectedField}
            onChange={(e) => {
              setSelectedField(e.target.value);
              setNewValue('');
            }}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Sélectionner un champ --</option>
            {fieldKeys.map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
        </div>

        {/* Current value (read-only) */}
        {selectedField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valeur actuelle
            </label>
            <div className="bg-gray-100 border border-gray-200 rounded px-3 py-2 text-sm text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
              {currentValue || <span className="italic text-gray-400">(vide)</span>}
            </div>
          </div>
        )}

        {/* New value */}
        {selectedField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nouvelle valeur
            </label>
            <textarea
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Entrez la nouvelle valeur..."
            />
          </div>
        )}

        {/* Reason */}
        {selectedField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Raison <span className="text-gray-400">(optionnel)</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Raison de la modification..."
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedField || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Enregistrement...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
